// University of California, Los Angeles: public college page (#2596),
// official facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UCLA's Common Data Set 2025-26 ("CDS 2025-26 - All Sections.pdf",
//    linked from Academic Planning and Budget). C1 lists Fall 2025 counts by
//    sex (men, women, unknown sex) with no printed total; the counts here are
//    their sums: 145,086 applied, 13,659 admitted, 6,553 enrolled. UCLA's
//    own Fall 2025 first-year profile prints 145,085 applicants, one fewer;
//    admits and enrollees match. The C1 residency table is reported as zeros,
//    so no residency rows. Parts of the PDF use a scrambled font; every
//    number used here is in clear text. C8A answers no to using SAT or ACT
//    scores. C21 and C22 answer no to early decision and early action.
//  - UCLA admissions pages (first-year applicants, first-year requirements,
//    Fall 2025 first-year profile with admit rates by school, supplemental
//    application deadlines). The first-year page says both "October 1 -
//    December 1" and "October 1 - November 30"; the UC systemwide dates page
//    and UCLA's supplemental page both say November 30, which is used here.
//  - UCLA Financial Aid and Scholarships, 2026-27 cost of attendance for new
//    students (in-state and nonresident totals as published).
//  - College Scorecard, school id 110662, read from the Scorecard school
//    page (the API's demo key was over its rate limit; same data).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UCLA Common Data Set 2025-2026',
        url: 'https://apb.ucla.edu/file/f8f5c864-93be-4b44-b646-7807a685749f',
        read: '2026-09-30',
    },
    classProfile: {
        label: 'UCLA Admission, First-Year Profile Fall 2025',
        url: 'https://admission.ucla.edu/apply/first-year/first-year-profile/2025',
        read: '2026-09-30',
    },
    firstYear: {
        label: 'UCLA Admission, First-Year Applicants',
        url: 'https://admission.ucla.edu/apply/first-year',
        read: '2026-09-30',
    },
    ucDates: {
        label: 'UC Admissions, First-Year Dates and Deadlines',
        url: 'https://admission.universityofcalifornia.edu/how-to-apply/applying-as-a-first-year/dates-and-deadlines.html',
        read: '2026-09-30',
    },
    tests: {
        label: 'UCLA Admission, First-Year Requirements',
        url: 'https://admission.ucla.edu/apply/first-year/first-year-requirements',
        read: '2026-09-30',
    },
    programs: {
        label: 'UCLA Admission, Supplemental Applications',
        url: 'https://admission.ucla.edu/apply/supplemental-applications',
        read: '2026-09-30',
    },
    coa: {
        label: 'UCLA Financial Aid, Cost of Attendance 2026-27',
        url: 'https://financialaid.ucla.edu/how-aid-works/cost-of-attendance',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110662-University-of-California-Los-Angeles',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-california-los-angeles',
    kbId: 'university_of_california_los_angeles',
    name: 'University of California, Los Angeles',
    shortName: 'UCLA',
    location: 'Los Angeles, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UCLA brand guidelines); decoration only.
    branding: {
        primary: '#2774AE',
        secondary: '#FFD100',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_los_angeles_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 145086,
        admitted: 13659,
        enrolled: 6553,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        year: 'Fall 2025',
        published: true,
        note: 'UCLA publishes first-year admit rates by the school you apply to, for Fall 2025, rounded as UCLA shows them. UCLA says that in the College of Letters and Science your choice of major neither helps nor hurts your application.',
        head: ['School', 'Applicants', 'Admits', 'Admit rate'],
        rows: [
            { name: 'The College (Letters and Science)', applied: 98459, admitted: 10788, rate: 11 },
            { name: 'Samueli School of Engineering', applied: 33052, admitted: 2241, rate: 6.8 },
            { name: 'School of Theater, Film and Television', applied: 3293, admitted: 140, rate: 4.3 },
            { name: 'School of the Arts and Architecture', applied: 3437, admitted: 177, rate: 5.1 },
            { name: 'Herb Alpert School of Music', applied: 1175, admitted: 282, rate: 24 },
            { name: 'School of Nursing', applied: 5669, admitted: 31, rate: 0.5 },
        ],
        source: 'classProfile',
        where: 'Overview by School Applied To',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UCLA offers neither. The Common Data Set answers no to both, and the admissions office says it does not offer early action or early decision for any application.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Majors in four schools outside the College need a supplemental application sent to the school, on top of the UC application:',
        rows: [
            { school: 'Architectural Studies, Art', requirement: 'Supplemental materials due January 20, 2027.' },
            { school: 'Computational Art, Dance, Design Media Arts, Games, World Arts and Cultures', requirement: 'Supplemental materials due December 15, 2026.' },
            { school: 'Music majors (Ethnomusicology, Global Jazz Studies, Music Composition, Music Education, Music Industry, Music Performance, Musicology)', requirement: 'Supplemental materials due December 4, 2026.' },
            { school: 'Film and Television, Theater', requirement: 'Supplemental materials due December 7, 2026.' },
            { school: 'Nursing', requirement: 'Supplemental materials due January 15, 2027.' },
        ],
        note: 'List a major in one of these schools as your first-choice major on the UC application. If you list it only as your alternate major, you are reviewed for your first-choice major alone.',
        source: 'programs',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application (one filing period)', deadline: 'November 30', decision: 'Late March', binding: false },
        ],
        note: 'You apply on the UC application, which opens August 1. UCLA admits first-year students for the fall term only.',
        source: 'firstYear',
        binding: 'Nothing is binding. For fall 2027 the University of California has one filing period, October 1 to November 30, with admission decisions in March.',
        bindingSource: 'ucDates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UCLA is test-blind: SAT and ACT scores are not considered for admission or scholarships, even if you send them. Scores you send may be used only to meet minimum requirements or for course placement after you enroll.',
        source: 'tests',
        cdsLine: 'The Common Data Set agrees: it answers no to using SAT or ACT scores in admission decisions.',
        cdsWhere: 'section C8',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and campus fees', amounts: [16430, 16430] },
            { item: 'Nonresident supplemental tuition', amounts: [0, 39270] },
            { item: 'Housing (on campus)', amounts: [12299, 12299] },
            { item: 'Food', amounts: [7568, 7568] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1588, 1588] },
            { item: 'Transportation', amounts: [969, 969] },
            { item: 'Personal', amounts: [2761, 2761] },
            { item: 'Health insurance', amounts: [3885, 3885] },
        ],
        totals: [45500, 84770],
        note: 'The budget for a new student living on campus. Health insurance can be waived if you have your own coverage.',
        source: 'coa',
        netPrice: {
            average: 14659,
            byIncome: [
                { income: '$0 to $30,000', amount: 7755 },
                { income: '$30,001 to $48,000', amount: 8643 },
                { income: '$48,001 to $75,000', amount: 10769 },
                { income: '$75,001 to $110,000', amount: 15256 },
                { income: 'Over $110,000', amount: 32922 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UCLA\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
